import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { CheckCircle } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getItemModelCatalogId,
    getItemModelCurrency,
    getItemModelIsSold,
    getItemModelSellerId,
    getItemModelSellerName,
    getItemSummaryBidderHasBid,
    getItemSummaryBidderMaxBid,
    getItemSummaryLeadingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getRegistrationBidLimit } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getTotalBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import BidLimitTooltip from './components/BidLimitTooltip';
import FormattedCurrency from '../Formatted/FormattedCurrency';
import styled from 'styled-components';
import useIsNarrowerThanCustom from '@/hooks/useIsNarrowerThan';

const customItemPageWidth = 900;

type Props = { className?: string; itemId: number };

const BidLimitMessage = ({ className, itemId }: Props) => {
    const { formatMessage, formatNumber } = useIntl();
    const dispatch = useAppDispatch();

    const sellerId = useAppSelector((state) => getItemModelSellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemModelSellerName(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const currency = useAppSelector((state) => getItemModelCurrency(state, itemId));
    const isSold = useAppSelector((state) => getItemModelIsSold(state, itemId));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const narrow = useIsNarrowerThanCustom(customItemPageWidth);
    const totalCatalogBidLimitBalance = useAppSelector((state) => getTotalBidLimitBalance(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const hasBidInThisCatalog = bidLimit !== totalCatalogBidLimitBalance || bidderHasBid;
    const maxedLimit = !Boolean(totalCatalogBidLimitBalance) || !Boolean(totalCatalogBidLimitBalance - bidderMaxBid);
    const winningBidIsOverBidLimit = leadingBid > totalCatalogBidLimitBalance - bidderMaxBid;

    const showSendSellerMessageModal = useCallback(() => {
        const messageDetails = {
            message: formatMessage(
                {
                    id: 'registration.sellerMessage.body',
                },
                {
                    bidLimit: formatNumber(bidLimit, {
                        currency,
                        maximumFractionDigits: 0,
                        minimumFractionDigits: 0,
                        style: 'currency',
                    }),
                }
            ),
        };
        const subject = formatMessage({
            id: 'registration.sellerMessage.subject',
        });

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [formatMessage, formatNumber, bidLimit, currency, dispatch, sellerId, sellerName]);

    // if the bidder has a bid limit, but the item is sold show approved.
    if (isSold) {
        return (
            <StyledBidLimitMessage className={className}>
                <FormattedMessage id="registration.sold_auction.approved" />
            </StyledBidLimitMessage>
        );
        // if the bidder has maxed out their bid limit
    } else if (maxedLimit) {
        return (
            <StyledBidLimitMessage className={className}>
                <StyledDiv>
                    <FormattedMessage
                        id="registration.auction_status_will_met"
                        values={{
                            bidLimit: (
                                <FormattedCurrency
                                    convert
                                    key="bid-limit-message-bid-limit"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={bidLimit}
                                />
                            ),
                        }}
                    />{' '}
                    <BidLimitTooltip itemId={itemId} />
                </StyledDiv>
                <InlineButton
                    onClick={showSendSellerMessageModal}
                    size="sm"
                >
                    {formatMessage({
                        id: 'registration.auction_status_request_text',
                    })}
                </InlineButton>
            </StyledBidLimitMessage>
        );
        // if the winning bid would put the user over their bid limit
    } else if (winningBidIsOverBidLimit) {
        return (
            <StyledBidLimitMessage className={className}>
                <StyledDiv>
                    <FormattedMessage
                        id="registration.auction_status_will_exceed"
                        values={{
                            bidLimit: (
                                <FormattedCurrency
                                    convert
                                    key="bid-limit-message-bid-limit"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={bidLimit}
                                />
                            ),
                        }}
                    />{' '}
                    <BidLimitTooltip itemId={itemId} />
                </StyledDiv>
                <InlineButton
                    onClick={showSendSellerMessageModal}
                    size="sm"
                >
                    {formatMessage({
                        id: 'registration.auction_status_request_text',
                    })}
                </InlineButton>
            </StyledBidLimitMessage>
        );
        // if the bidder has bid in this catalog
    } else if (hasBidInThisCatalog) {
        return (
            <StyledBidLimitMessage className={className}>
                <FormattedMessage
                    id="registration.auction_status_container"
                    values={{
                        first: (
                            <>
                                <FormattedMessage
                                    id="registration.auction_status_approved_first"
                                    values={{
                                        limit: (
                                            <FormattedCurrency
                                                convert
                                                key="bid-limit-message-bid-limit"
                                                noFraction
                                                sourceCurrency={currency}
                                                value={bidLimit}
                                            />
                                        ),
                                    }}
                                />
                                {!narrow && (
                                    <>
                                        {' '}
                                        <BidLimitTooltip itemId={itemId} />
                                        <br />
                                    </>
                                )}
                            </>
                        ),
                        second: (
                            <>
                                <FormattedMessage
                                    id="registration.auction_status_approved_second"
                                    values={{
                                        balance: (
                                            <FormattedCurrency
                                                convert
                                                key="bid-limit-message-balance"
                                                noFraction
                                                sourceCurrency={currency}
                                                value={totalCatalogBidLimitBalance}
                                            />
                                        ),
                                    }}
                                />{' '}
                                {narrow && <BidLimitTooltip itemId={itemId} />}
                            </>
                        ),
                    }}
                />
            </StyledBidLimitMessage>
        );
    }

    // otherwise just show that they are approved with a bid limit and a friendly checkmark
    return (
        <StyledBidLimitMessage className={className}>
            <FormattedMessage
                id="registration.auction_status_approved_not_bid"
                values={{
                    icon: (
                        <CheckCircle
                            className="align-middle"
                            color="success"
                            key="bid-limit-message-icon"
                            size="sm"
                            style="fill"
                        />
                    ),
                    limit: (
                        <FormattedCurrency
                            convert
                            key="bid-limit-message-limit"
                            noFraction
                            sourceCurrency={currency}
                            value={bidLimit}
                        />
                    ),
                }}
            />
        </StyledBidLimitMessage>
    );
};

export default BidLimitMessage;

const StyledBidLimitMessage = styled(BodySecondary)`
    margin: 32px 0 0;
    display: inline-block;

    [data-testid='popover'] {
        display: inline-block;
        height: 20px;
        vertical-align: middle;
        & > div {
            height: 20px;
        }
    }

    @media print {
        display: none;
    }
`;

const StyledDiv = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing.xs};
`;
